import { showAvatars, showMedia, updateMyUserInfo } from "@utils/app";
import { isBrowser } from "@utils/browser";
import { numToSI } from "@utils/helpers";
import {
  amAdmin,
  moderatesPrivateCommunity,
  moderatesSomething,
} from "@utils/roles";
import classNames from "classnames";
import { Component, InfernoNode, createRef } from "inferno";
import { NavLink } from "inferno-router";
import { GetSiteResponse, MyUserInfo } from "lemmy-js-client";
import {
  I18NextService,
  UserService,
  UnreadCounterService,
} from "../../services";
import { allLanguages } from "../../services/i18n-languages";
import { Icon } from "../common/icon";
import { PictrsImage } from "../common/pictrs-image";
import { getStaticDir } from "@utils/env";
import { aimooT } from "@utils/aimoo-i18n";
import { aimooMainSiteHref, setAimooLocaleCookie } from "@utils/aimoo-locale";
import { Subscription } from "rxjs";
import { tippyMixin } from "../mixins/tippy-mixin";
import { HttpService } from "../../services/HttpService";

const AIMOO_BRAND_LOGO = `${getStaticDir()}/assets/images/aimoo-logo.png`;

/** Main Aimoo site paths (outside the /community SPA base). Locale prefix applied at render. */
const AIMOO_SITE_PATHS = {
  home: "/",
  market: "/market",
  demand: "/demand",
  publishDemand: "/demand",
  messages: "/im/app",
  docs: "/docs",
  login: "/auth/login",
  console: "/dashboard",
  dashboard: "/dashboard",
  wallet: "/wallet",
  profile: "/profile",
  orders: "/orders",
  myAgents: "/my-agents",
  personalAssistant: "/personal-assistant",
  logout: "/logout",
} as const;

function aimooSite(path: keyof typeof AIMOO_SITE_PATHS): string {
  return aimooMainSiteHref(
    AIMOO_SITE_PATHS[path],
    I18NextService.i18n.language,
  );
}

type AimooLangOption = {
  code: string;
  flag: string;
  name: string;
  short: string;
};

const AIMOO_LANG_OPTIONS: AimooLangOption[] = [
  { code: "zh-CN", flag: "🇨🇳", name: "简体中文", short: "中" },
  { code: "en", flag: "🇺🇸", name: "English", short: "EN" },
  { code: "es", flag: "🇪🇸", name: "Español", short: "ES" },
  { code: "fr", flag: "🇫🇷", name: "Français", short: "FR" },
  { code: "pt-BR", flag: "🇧🇷", name: "Português (Brasil)", short: "PT" },
  { code: "de", flag: "🇩🇪", name: "Deutsch", short: "DE" },
  { code: "ja", flag: "🇯🇵", name: "日本語", short: "JA" },
  { code: "ko", flag: "🇰🇷", name: "한국어", short: "KO" },
];

interface NavbarProps {
  siteRes?: GetSiteResponse;
  myUserInfo: MyUserInfo | undefined;
}

interface NavbarState {
  onSiteBanner?(url: string): unknown;
  unreadNotifsCount: number;
  unreadReportCount: number;
  unreadApplicationCount: number;
  unreadPendingFollowsCount: number;
  langMenuOpen: boolean;
}

@tippyMixin
export class Navbar extends Component<NavbarProps, NavbarState> {
  collapseButtonRef = createRef<HTMLButtonElement>();
  mobileMenuRef = createRef<HTMLDivElement>();
  langMenuRef = createRef<HTMLDivElement>();
  unreadNotifsCountSubscription: Subscription;
  unreadReportCountSubscription: Subscription;
  unreadApplicationCountSubscription: Subscription;
  unreadPendingFollowsSubscription: Subscription;

  state: NavbarState = {
    unreadNotifsCount: 0,
    unreadReportCount: 0,
    unreadApplicationCount: 0,
    unreadPendingFollowsCount: 0,
    langMenuOpen: false,
  };

  componentWillMount() {
    // Subscribe to jwt changes
    if (isBrowser()) {
      // On the first load, check the unreads
      UnreadCounterService.Instance.configure(this.props.myUserInfo);
      this.unreadNotifsCountSubscription =
        UnreadCounterService.Instance.notificationCount.subscribe(
          unreadNotifsCount => this.setState({ unreadNotifsCount }),
        );
      this.unreadReportCountSubscription =
        UnreadCounterService.Instance.unreadReportCount.subscribe(
          unreadReportCount => this.setState({ unreadReportCount }),
        );
      if (moderatesSomething(this.props.myUserInfo)) {
        this.unreadApplicationCountSubscription =
          UnreadCounterService.Instance.unreadApplicationCount.subscribe(
            unreadApplicationCount =>
              this.setState({ unreadApplicationCount }),
          );
      }
      if (moderatesPrivateCommunity(this.props.myUserInfo)) {
        this.unreadPendingFollowsSubscription =
          UnreadCounterService.Instance.pendingFollowCount.subscribe(
            unreadPendingFollows =>
              this.setState({
                unreadPendingFollowsCount: unreadPendingFollows,
              }),
          );
      }

      document.addEventListener("mouseup", this.outsideMenuClickHandler);
    }
  }

  outsideMenuClickHandler = (e: MouseEvent) => {
    const target = e.target as Node | null;
    if (!this.mobileMenuRef.current?.contains(target)) {
      handleCollapseClick(this);
    }
    if (!this.langMenuRef.current?.contains(target)) {
      if (this.state.langMenuOpen) {
        this.setState({ langMenuOpen: false });
      }
    }
  };

  componentWillUnmount() {
    document.removeEventListener("mouseup", this.outsideMenuClickHandler);
    this.unreadNotifsCountSubscription.unsubscribe();
    this.unreadReportCountSubscription.unsubscribe();
    if (moderatesSomething(this.props.myUserInfo)) {
      this.unreadApplicationCountSubscription.unsubscribe();
    }
    if (moderatesPrivateCommunity(this.props.myUserInfo)) {
      this.unreadPendingFollowsSubscription.unsubscribe();
    }
  }

  get currentLangOption(): AimooLangOption {
    const code = I18NextService.i18n.language || "zh-CN";
    const exact = AIMOO_LANG_OPTIONS.find(l => l.code === code);
    if (exact) {
      return exact;
    }
    const prefix = code.split("-")[0] ?? code;
    return (
      AIMOO_LANG_OPTIONS.find(
        l =>
          l.code === prefix ||
          l.code.startsWith(`${prefix}-`) ||
          (prefix === "zh" && l.code === "zh-CN"),
      ) ?? AIMOO_LANG_OPTIONS[0]!
    );
  }

  render() {
    const siteView = this.props.siteRes?.site_view;
    const person = this.props.myUserInfo?.local_user_view.person;
    const currentLang = this.currentLangOption;

    return (
      <div className="app-navbar">
        <nav
          className="navbar navbar-expand-md navbar-light p-0 aimoo-nav-inner"
          id="navbar"
        >
          {/* Domain root (Aimoo home), not the /community SPA base path */}
          <a
            id="navTitle"
            href={aimooSite("home")}
            title={siteView?.site.summary ?? siteView?.site.name ?? "Aimoo"}
            className="d-flex align-items-center navbar-brand navbar-brand-logo"
            onMouseUp={() => handleCollapseClick(this)}
          >
            <img
              src={AIMOO_BRAND_LOGO}
              alt="Aimoo"
              className="aimoo-brand-logo"
              height={44}
            />
          </a>
          {person && (
            <ul className="navbar-nav d-flex flex-row ms-auto d-md-none">
              <li id="navMessages" className="nav-item nav-item-icon">
                <NavLink
                  to="/notifications"
                  className="p-1 nav-link border-0 nav-messages"
                  title={unreadNotificationsCount(this.state.unreadNotifsCount)}
                  onMouseUp={() => handleCollapseClick(this)}
                >
                  <Icon icon="bell" />
                  {this.state.unreadNotifsCount > 0 && (
                    <span className="mx-1 badge rounded-pill text-bg-danger">
                      {numToSI(this.state.unreadNotifsCount)}
                    </span>
                  )}
                </NavLink>
              </li>
              {this.state.unreadReportCount > 0 &&
                moderatesSomething(this.props.myUserInfo) && (
                  <li className="nav-item nav-item-icon">
                    <NavLink
                      to="/reports"
                      className="p-1 nav-link border-0"
                      title={unreadReportsCount(this.state.unreadReportCount)}
                      onMouseUp={() => handleCollapseClick(this)}
                    >
                      <Icon icon="shield" />
                      <span className="mx-1 badge rounded-pill text-bg-danger">
                        {numToSI(this.state.unreadReportCount)}
                      </span>
                    </NavLink>
                  </li>
                )}
              {this.state.unreadApplicationCount > 0 &&
                amAdmin(this.props.myUserInfo) && (
                  <li className="nav-item nav-item-icon">
                    <NavLink
                      to="/registration_applications"
                      className="p-1 nav-link border-0"
                      title={unreadApplicationCount(
                        this.state.unreadApplicationCount,
                      )}
                      onMouseUp={() => handleCollapseClick(this)}
                    >
                      <Icon icon="clipboard" />
                      <span className="mx-1 badge rounded-pill text-bg-danger">
                        {numToSI(this.state.unreadApplicationCount)}
                      </span>
                    </NavLink>
                  </li>
                )}
              {this.state.unreadPendingFollowsCount > 0 &&
                moderatesPrivateCommunity(this.props.myUserInfo) && (
                  <li className="nav-item nav-item-icon">
                    <NavLink
                      to="/pending_follows"
                      className="p-1 nav-link border-0"
                      title={pendingPrivateCommunityFollowsCount(
                        this.state.unreadPendingFollowsCount,
                      )}
                      onMouseUp={() => handleCollapseClick(this)}
                    >
                      <Icon icon="lock" />
                      <span className="mx-1 badge rounded-pill text-bg-danger">
                        {numToSI(this.state.unreadPendingFollowsCount)}
                      </span>
                    </NavLink>
                  </li>
                )}
            </ul>
          )}
          <button
            className="navbar-toggler border-0 p-1"
            type="button"
            aria-label="menu"
            data-tippy-content={I18NextService.i18n.t("expand_here")}
            data-bs-toggle="collapse"
            data-bs-target="#navbarDropdown"
            aria-controls="navbarDropdown"
            aria-expanded="false"
            ref={this.collapseButtonRef}
          >
            <Icon icon="menu" />
          </button>
          <div
            className="collapse navbar-collapse"
            id="navbarDropdown"
            ref={this.mobileMenuRef}
          >
            <ul
              id="navbarLinks"
              className="navbar-nav aimoo-main-nav"
              aria-label={aimooT("nav_global")}
            >
              <AimooExternalNavLink
                href={aimooSite("market")}
                icon="storefront"
                label={aimooT("agent_market")}
                onClick={() => handleCollapseClick(this)}
              />
              <AimooExternalNavLink
                href={aimooSite("demand")}
                icon="add_task"
                label={aimooT("demand_plaza")}
                onClick={() => handleCollapseClick(this)}
              />
              <AimooExternalNavLink
                href={
                  person
                    ? aimooSite("messages")
                    : `${aimooSite("login")}?redirect=${encodeURIComponent(aimooSite("messages"))}`
                }
                icon="chat"
                label={aimooT("messages")}
                onClick={() => handleCollapseClick(this)}
              />
              <AimooNavLink
                to="/"
                icon="groups"
                label={aimooT("community")}
                onClick={() => handleCollapseClick(this)}
              />
              <AimooExternalNavLink
                href={aimooSite("docs")}
                icon="terminal"
                label={aimooT("developers")}
                muted
                onClick={() => handleCollapseClick(this)}
              />
            </ul>
            <ul id="navbarIcons" className="navbar-nav aimoo-nav-actions">
              <li className="nav-item aimoo-language-switch">
                <div className="aimoo-language-menu" ref={this.langMenuRef}>
                  <button
                    type="button"
                    className="aimoo-language-switch__btn"
                    aria-label={`Language: ${currentLang.name}`}
                    aria-expanded={this.state.langMenuOpen ? "true" : "false"}
                    aria-haspopup="listbox"
                    onClick={() =>
                      this.setState(s => ({ langMenuOpen: !s.langMenuOpen }))
                    }
                  >
                    <span>
                      {currentLang.flag} {currentLang.short}
                    </span>
                    <i className="aimoo-language-switch__caret" aria-hidden />
                  </button>
                  {this.state.langMenuOpen && (
                    <div
                      className="aimoo-language-dropdown"
                      role="listbox"
                      aria-label={`Language: ${currentLang.name}`}
                    >
                      {AIMOO_LANG_OPTIONS.filter(opt =>
                        allLanguages.some(
                          l =>
                            l.code === opt.code ||
                            l.code.startsWith(opt.code.split("-")[0] ?? ""),
                        ),
                      ).map(opt => (
                        <button
                          key={opt.code}
                          type="button"
                          role="option"
                          aria-selected={opt.code === currentLang.code}
                          className={classNames("aimoo-language-option", {
                            "aimoo-language-option--active":
                              opt.code === currentLang.code,
                          })}
                          onClick={() => handleLanguageSelect(this, opt.code)}
                        >
                          <span
                            className="aimoo-language-option__flag"
                            aria-hidden
                          >
                            {opt.flag}
                          </span>
                          <span className="aimoo-language-option__copy">
                            <strong>{opt.name}</strong>
                            <small>{opt.short}</small>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </li>
              {person && (
                <li
                  className="nav-item aimoo-ops-links"
                  aria-label={aimooT("nav_account_shortcuts")}
                >
                  <a
                    href={aimooSite("console")}
                    className="aimoo-ops-link"
                    title={aimooT("nav_console")}
                    aria-label={aimooT("nav_console")}
                    onMouseUp={() => handleCollapseClick(this)}
                  >
                    <AimooMaterialIcon name="apps" />
                    <span>{aimooT("nav_console")}</span>
                  </a>
                  <a
                    href={aimooSite("wallet")}
                    className="aimoo-ops-link"
                    title={aimooT("nav_wallet")}
                    aria-label={aimooT("nav_wallet")}
                    onMouseUp={() => handleCollapseClick(this)}
                  >
                    <AimooMaterialIcon name="account_balance_wallet" />
                    <span>{aimooT("nav_wallet")}</span>
                  </a>
                </li>
              )}
              {!person && (
                <li className="nav-item">
                  <a
                    href={aimooSite("login")}
                    className="nav-link aimoo-login-link"
                    title={aimooT("login")}
                    onMouseUp={() => handleCollapseClick(this)}
                  >
                    {aimooT("login")}
                  </a>
                </li>
              )}
              <li className="nav-item aimoo-publish-item">
                <a
                  href={aimooSite("publishDemand")}
                  className="aimoo-publish-link"
                  title={aimooT("publish_demand")}
                  onMouseUp={() => handleCollapseClick(this)}
                >
                  {aimooT("publish_demand")}
                </a>
              </li>
              {person && (
                <>
                  {(this.state.unreadReportCount > 0 ||
                    this.state.unreadApplicationCount > 0 ||
                    this.state.unreadPendingFollowsCount > 0) && (
                    <>
                      {moderatesSomething(this.props.myUserInfo) &&
                        this.state.unreadReportCount > 0 && (
                          <li id="navModeration" className="nav-item">
                            <NavLink
                              className="nav-link d-inline-flex align-items-center"
                              to="/reports"
                              title={unreadReportsCount(
                                this.state.unreadReportCount,
                              )}
                              onMouseUp={() => handleCollapseClick(this)}
                            >
                              <Icon icon="shield" />
                              <span className="mx-1 badge rounded-pill text-bg-danger">
                                {numToSI(this.state.unreadReportCount)}
                              </span>
                            </NavLink>
                          </li>
                        )}
                      {amAdmin(this.props.myUserInfo) &&
                        this.state.unreadApplicationCount > 0 && (
                          <li id="navApplications" className="nav-item">
                            <NavLink
                              to="/registration_applications"
                              className="nav-link d-inline-flex align-items-center"
                              title={unreadApplicationCount(
                                this.state.unreadApplicationCount,
                              )}
                              onMouseUp={() => handleCollapseClick(this)}
                            >
                              <Icon icon="clipboard" />
                              <span className="mx-1 badge rounded-pill text-bg-danger">
                                {numToSI(this.state.unreadApplicationCount)}
                              </span>
                            </NavLink>
                          </li>
                        )}
                      {moderatesPrivateCommunity(this.props.myUserInfo) &&
                        this.state.unreadPendingFollowsCount > 0 && (
                          <li className="nav-item">
                            <NavLink
                              to="/pending_follows"
                              className="nav-link d-inline-flex align-items-center"
                              title={pendingPrivateCommunityFollowsCount(
                                this.state.unreadPendingFollowsCount,
                              )}
                              onMouseUp={() => handleCollapseClick(this)}
                            >
                              <Icon icon="lock" />
                              <span className="mx-1 badge rounded-pill text-bg-danger">
                                {numToSI(this.state.unreadPendingFollowsCount)}
                              </span>
                            </NavLink>
                          </li>
                        )}
                    </>
                  )}
                  <li id="dropdownUser" className="dropdown aimoo-user-menu">
                    <button
                      type="button"
                      className="btn aimoo-user-menu__trigger"
                      aria-label={aimooT("nav_user_menu")}
                      aria-expanded="false"
                      data-bs-toggle="dropdown"
                      data-bs-display="static"
                    >
                      {showAvatars(this.props.myUserInfo) &&
                      showMedia(this.props.myUserInfo) &&
                      person.avatar ? (
                        <PictrsImage src={person.avatar} type="icon" />
                      ) : (
                        <span
                          className="aimoo-user-menu__avatar-fallback"
                          aria-hidden
                        >
                          {(person.display_name ?? person.name)
                            .trim()
                            .charAt(0)
                            .toUpperCase() || "U"}
                        </span>
                      )}
                      <i className="aimoo-user-menu__chevron" aria-hidden />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end aimoo-user-menu__dropdown">
                      <li>
                        <a
                          className="aimoo-user-menu__item"
                          href={aimooSite("dashboard")}
                          onClick={() => handleCollapseClick(this)}
                        >
                          {aimooT("nav_dashboard")}
                        </a>
                      </li>
                      <li>
                        <a
                          className="aimoo-user-menu__item"
                          href={aimooSite("profile")}
                          onClick={() => handleCollapseClick(this)}
                        >
                          {aimooT("nav_profile")}
                        </a>
                      </li>
                      <li>
                        <a
                          className="aimoo-user-menu__item"
                          href={aimooSite("wallet")}
                          onClick={() => handleCollapseClick(this)}
                        >
                          {aimooT("nav_wallet")}
                        </a>
                      </li>
                      <li>
                        <a
                          className="aimoo-user-menu__item"
                          href={aimooSite("orders")}
                          onClick={() => handleCollapseClick(this)}
                        >
                          {aimooT("nav_orders")}
                        </a>
                      </li>
                      <li>
                        <a
                          className="aimoo-user-menu__item"
                          href={aimooSite("myAgents")}
                          onClick={() => handleCollapseClick(this)}
                        >
                          {aimooT("nav_my_agents")}
                        </a>
                      </li>
                      <li>
                        <a
                          className="aimoo-user-menu__item"
                          href={aimooSite("personalAssistant")}
                          onClick={() => handleCollapseClick(this)}
                        >
                          {aimooT("nav_personal_assistant")}
                        </a>
                      </li>
                      <li className="aimoo-user-menu__sep" aria-hidden />
                      <li>
                        <NavLink
                          className="aimoo-user-menu__item"
                          to={`/u/${person.name}`}
                          onClick={() => handleCollapseClick(this)}
                        >
                          {I18NextService.i18n.t("posts")}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          className="aimoo-user-menu__item"
                          to="/settings"
                          onClick={() => handleCollapseClick(this)}
                        >
                          {I18NextService.i18n.t("settings")}
                        </NavLink>
                      </li>
                      {moderatesSomething(this.props.myUserInfo) && (
                        <li>
                          <NavLink
                            className="aimoo-user-menu__item"
                            to="/reports"
                            onClick={() => handleCollapseClick(this)}
                          >
                            {I18NextService.i18n.t("reports")}
                          </NavLink>
                        </li>
                      )}
                      {amAdmin(this.props.myUserInfo) && (
                        <>
                          <li className="aimoo-user-menu__sep" aria-hidden />
                          <li>
                            <NavLink
                              to="/admin"
                              className="aimoo-user-menu__item"
                              onClick={() => handleCollapseClick(this)}
                            >
                              {aimooT("nav_admin")}
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              to="/admin/communities"
                              className="aimoo-user-menu__item"
                              onClick={() => handleCollapseClick(this)}
                            >
                              {aimooT("nav_community_admin")}
                            </NavLink>
                          </li>
                          <li>
                            <NavLink
                              className="aimoo-user-menu__item"
                              to="/registration_applications"
                              onClick={() => handleCollapseClick(this)}
                            >
                              {I18NextService.i18n.t(
                                "registration_applications",
                              )}
                            </NavLink>
                          </li>
                        </>
                      )}
                      {moderatesPrivateCommunity(this.props.myUserInfo) && (
                        <li>
                          <NavLink
                            className="aimoo-user-menu__item"
                            to="/pending_follows"
                            onClick={() => handleCollapseClick(this)}
                          >
                            {I18NextService.i18n.t(
                              "community_pending_follows",
                            )}
                          </NavLink>
                        </li>
                      )}
                      <li>
                        {amAdmin(this.props.myUserInfo) ? (
                          <button
                            type="button"
                            className="aimoo-user-menu__item aimoo-user-menu__logout"
                            onClick={() => handleLogOut(this)}
                          >
                            {aimooT("nav_logout")}
                          </button>
                        ) : (
                          <a
                            href={aimooSite("logout")}
                            className="aimoo-user-menu__item aimoo-user-menu__logout"
                            onClick={() => handleCollapseClick(this)}
                          >
                            {aimooT("nav_logout")}
                          </a>
                        )}
                      </li>
                    </ul>
                  </li>
                </>
              )}
            </ul>
          </div>
        </nav>
      </div>
    );
  }
}

interface AimooNavLinkProps {
  to: string;
  icon: string;
  label: string;
  onClick: () => void;
}

function AimooMaterialIcon({ name }: { name: string }) {
  return (
    <span className="material-symbols-outlined aimoo-nav-icon" aria-hidden>
      {name}
    </span>
  );
}

function AimooNavLink({ to, icon, label, onClick }: AimooNavLinkProps) {
  return (
    <li className="nav-item">
      <NavLink
        to={to}
        className="nav-link aimoo-nav-link"
        title={label}
        onMouseUp={onClick}
      >
        <AimooMaterialIcon name={icon} />
        <span className="aimoo-nav-label">{label}</span>
      </NavLink>
    </li>
  );
}

interface AimooExternalNavLinkProps {
  href: string;
  icon: string;
  label: string;
  muted?: boolean;
  onClick: () => void;
}

function AimooExternalNavLink({
  href,
  icon,
  label,
  muted,
  onClick,
}: AimooExternalNavLinkProps): InfernoNode {
  return (
    <li className="nav-item">
      <a
        href={href}
        className={classNames("nav-link aimoo-nav-link", {
          "aimoo-nav-link--muted": muted,
        })}
        title={label}
        onMouseUp={onClick}
      >
        <AimooMaterialIcon name={icon} />
        <span className="aimoo-nav-label">{label}</span>
      </a>
    </li>
  );
}

function handleCollapseClick(i: Navbar) {
  if (
    i.collapseButtonRef.current?.attributes &&
    i.collapseButtonRef.current?.attributes.getNamedItem("aria-expanded")
      ?.value === "true"
  ) {
    i.collapseButtonRef.current?.click();
  }
}

async function handleLogOut(i: Navbar) {
  await UserService.Instance.logout();
  handleCollapseClick(i);
}

async function handleLanguageSelect(i: Navbar, code: string) {
  i.setState({ langMenuOpen: false });
  setAimooLocaleCookie(code);

  const langs =
    typeof navigator !== "undefined" ? navigator.languages : ([code] as const);
  await I18NextService.reconfigure(langs, code);

  // Persist for logged-in Lemmy users so settings stay in sync.
  if (i.props.myUserInfo) {
    try {
      const saveRes = await HttpService.client.saveUserSettings({
        interface_language: code,
      });
      if (saveRes.state === "success") {
        const userRes = await HttpService.client.getMyUser();
        if (userRes.state === "success") {
          updateMyUserInfo(userRes.data);
        }
      }
    } catch {
      // Cookie + local i18n already applied; ignore API errors.
    }
  }

  handleCollapseClick(i);
}

function unreadReportsCount(unreadReportCount: number) {
  return I18NextService.i18n.t("unread_reports", {
    count: Number(unreadReportCount),
    formattedCount: numToSI(unreadReportCount),
  });
}

function unreadApplicationCount(unreadApplicationCount: number) {
  return I18NextService.i18n.t("unread_registration_applications", {
    count: Number(unreadApplicationCount),
    formattedCount: numToSI(unreadApplicationCount),
  });
}

function pendingPrivateCommunityFollowsCount(
  unreadPendingFollowsCount: number,
) {
  return I18NextService.i18n.t("pending_private_community_follows", {
    count: Number(unreadPendingFollowsCount),
    formattedCount: numToSI(unreadPendingFollowsCount),
  });
}

function unreadNotificationsCount(unreadNotifsCount: number) {
  return I18NextService.i18n.t("unread_messages", {
    count: Number(unreadNotifsCount),
    formattedCount: numToSI(unreadNotifsCount),
  });
}
